"use client";

import type { ComponentProps } from "react";
import LazyVideoPlayer from "@components/core/LazyVideoPlayer";
import { useAnalytics } from "@ui/hooks/useAnalytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { useCallback, useEffect, useRef } from "react";

interface EventResourceVideoPlayerProps extends Omit<ComponentProps<typeof LazyVideoPlayer>, "onPlay" | "onEnded"> {
  /** Event slug for tracking */
  eventSlug: string;
  /** Resource slug for tracking */
  resourceSlug: string;
  /** Resource title for tracking */
  resourceTitle: string;
}

export function EventResourceVideoPlayer({
  eventSlug,
  resourceSlug,
  resourceTitle,
  ...videoProps
}: EventResourceVideoPlayerProps) {
  const { captureEvent } = useAnalytics();
  const hasPlaybackStarted = useRef(false);

  useEffect(() => {
    hasPlaybackStarted.current = false;
  }, [resourceSlug]);

  const handlePlay = useCallback(() => {
    if (hasPlaybackStarted.current) return;
    hasPlaybackStarted.current = true;

    captureEvent(EventName.mw_event_resource_playback_started, {
      event_slug: eventSlug,
      resource_slug: resourceSlug,
      resource_title: resourceTitle,
    });
  }, [captureEvent, eventSlug, resourceSlug, resourceTitle]);

  const handleEnded = useCallback(() => {
    captureEvent(EventName.mw_event_resource_playback_ended, {
      event_slug: eventSlug,
      resource_slug: resourceSlug,
      resource_title: resourceTitle,
    });
  }, [captureEvent, eventSlug, resourceSlug, resourceTitle]);

  return <LazyVideoPlayer {...videoProps} onPlay={handlePlay} onEnded={handleEnded} />;
}
